import './SADeleteUserConfirmationModal.scss'
import {Button} from "../../../../Form/Button/Button";
import {buttonColors} from "../../../../Pickers/InputTheme";
import React, {useContext, useEffect, useState, useMemo} from "react";
import {Modal} from "../../../../Smart/Modal/Modal";
import {SA_deleteUsers} from "../../../../../Services/SA_api";
import {GridReactContext} from "../../../../../Utils/DataGridWrapper/GridContext";
import {I18nReactContext} from "../../../../../contexts/I18nContext";
import {DataTable} from "../../../../DataTable/DataTable";
import {ScrollableListCell} from "../../../../../Utils/ScrollableListCell/ScrollableListCell";
import {get} from "lodash";

/**
 * Modal component for Super Admin to confirm bulk user deletion.
 * Uses locally available data from the grid instead of fetching from API.
 * Calls the Super Admin specific API endpoint.
 *
 * @param {object} params - The component parameters
 * @param {boolean} params.deleteUserConfirmationModalOpen - Whether the modal is open
 * @param {function} params.onCloseDeleteUserConfirmationModal - Function to close the modal
 * @param {function} params.onSuccess - Function to call on successful deletion
 * @returns {JSX.Element} The SA delete user confirmation modal
 */
export const SADeleteUserConfirmationModal = ({
    deleteUserConfirmationModalOpen,
    onCloseDeleteUserConfirmationModal,
    onSuccess
}) => {
    const [deleting, setDeleting] = useState(false)
    const [error, setError] = useState(null)
    const gridContext = useContext(GridReactContext)
    const {i18n} = useContext(I18nReactContext)

    // Memoize selectionModel to prevent unnecessary re-renders
    const selectionModel = useMemo(() => gridContext.selectionModel, [gridContext.selectionModel])

    // Use selectedRows which contains the FULL data for selected users
    // DataGridWrapper updates this whenever selection changes
    const usersToDeleteWithData = useMemo(() => {
        // gridContext.selectedRows already contains the full user objects for selected IDs
        return gridContext.selectedRows || [];
    }, [gridContext.selectedRows])

    // Reset error when modal closes
    useEffect(() => {
        if (!deleteUserConfirmationModalOpen) {
            setError(null);
        }
    }, [deleteUserConfirmationModalOpen])

    const onDeleteUsers = async () => {
        try {
            setDeleting(true)
            setError(null)
            // Use Super Admin specific API endpoint
            await SA_deleteUsers({userIds: selectionModel})
            gridContext.triggerTableRefresh()
            onSuccess && onSuccess()
        }
        catch (e) {
            console.error('Error deleting users:', e)
            setError(i18n.translate('SADeleteUsersModal.deleteError'))
            setDeleting(false)
        }

    }
    return <Modal
        open={deleteUserConfirmationModalOpen}
        onClose={onCloseDeleteUserConfirmationModal}
        data-testid="sa-delete-confirmation-modal">
        <div className="SADeleteUsersModal modalWrapper">
            <div className="modalTitle">
                <div>{i18n.translate('SADeleteUsersModal.title')}</div>
            </div>
            <div className="modalContent">
                {/* Error State */}
                {error && (
                    <div data-testid="error-message" className="error-message">
                        {error}
                    </div>
                )}

                {/* Content - instantly available, no loading needed! */}
                {!error && (
                            <>
                                <div className="message">{i18n.translate('SADeleteUsersModal.message', {context: {userCount: selectionModel.length}})}</div>
                                <DataTable
                                    columns={[
                                        { key: 'firstName', label: i18n.translate('SADeleteUsersModal.firstName'), width: 150 },
                                        { key: 'lastName', label: i18n.translate('SADeleteUsersModal.lastName'), width: 150 },
                                        { key: 'email', label: i18n.translate('SADeleteUsersModal.email'), width: 300 },
                                        { key: 'phone', label: i18n.translate('SADeleteUsersModal.phone'), width: 170 },
                                        { key: 'employeeId', label: i18n.translate('SADeleteUsersModal.employeeID'), width: 150 },
                                        { key: 'idNumber', label: i18n.translate('SADeleteUsersModal.idNumber'), width: 150 },
                                        {
                                            key: 'organizations',
                                            label: i18n.translate('SADeleteUsersModal.organizations'),
                                            width: 300,
                                            render: (user) => {
                                                const organizations = get(user, 'row.organizations', []);
                                                if (organizations.length === 0) {
                                                    return '-';
                                                }
                                                const organizationNames = organizations.map((org) => get(org, 'orgName', '')).join(', ');
                                                return <ScrollableListCell value={organizationNames} />;
                                            }
                                        }
                                    ]}
                                    data={usersToDeleteWithData}
                                    getRowKey={(row) => row.id}
                                    className="usersToDelete"
                                />
                            </>
                )}
            </div>
            <div className="modalFooter">
                <Button
                    data-testid="cancel-delete-button"
                    color={buttonColors.YELLOW}
                    onClick={onCloseDeleteUserConfirmationModal}
                    disabled={deleting}
                    text={i18n.translate('general.close')}
                />
                <Button
                    data-testid="confirm-delete-button"
                    disabled={(!usersToDeleteWithData || usersToDeleteWithData.length === 0) || deleting}
                    color={buttonColors.GREY}
                    onClick={onDeleteUsers}
                    text={deleting ? i18n.translate('SADeleteUsersModal.deleting') : i18n.translate('SADeleteUsersModal.deleteUsers')}
                />
            </div>
        </div>
    </Modal>
}
